import { OAuthExtension } from '@magic-ext/oauth';
import { Connector, normalizeChainId, UserRejectedRequestError, } from '@wagmi/core';
import { ethers } from 'ethers';
import { getAddress } from 'ethers/lib/utils';
import { Magic } from 'magic-sdk';
import { createModal } from './modal/view';
const IS_SERVER = typeof window === 'undefined';
export class MagicConnector extends Connector {
    ready = !IS_SERVER;
    id = 'magic';
    name = 'Magic';
    provider;
    magicSDK;
    isModalOpen = false;
    magicOptions;
    accentColor;
    isDarkMode;
    customLogo;
    customHeaderText;
    oauthProviders;
    oauthCallbackUrl;
    constructor(config) {
        super(config);
        this.magicOptions = config.options;
        this.accentColor = config.options.accentColor;
        this.isDarkMode = config.options.isDarkMode;
        this.customLogo = config.options.customLogo;
        this.customHeaderText = config.options.customHeaderText;
        this.oauthProviders = config.options.oauthOptions?.providers || [];
        this.oauthCallbackUrl = config.options.oauthOptions?.callbackUrl;
    }
    async connect() {
        try {
            const provider = await this.getProvider();
            if (provider.on) {
                provider.on('accountsChanged', this.onAccountsChanged);
                provider.on('chainChanged', this.onChainChanged);
                provider.on('disconnect', this.onDisconnect);
            }
            // Check if there is a user logged in
            const isAuthenticated = await this.isAuthorized();
            // if there is a user logged in, return the user
            if (isAuthenticated) {
                return {
                    provider,
                    chain: {
                        id: 0,
                        unsupported: false,
                    },
                    account: await this.getAccount(),
                };
            }
            // open the modal and process the magic login steps
            if (!this.isModalOpen) {
                const output = await this.getUserDetailsByForm();
                const magic = this.getMagicSDK();
                // LOGIN WITH MAGIC LINK WITH OAUTH PROVIDER
                if (output.oauthProvider) {
                    await magic.oauth.loginWithRedirect({
                        provider: output.oauthProvider,
                        redirectURI: this.oauthCallbackUrl || window.location.href,
                    });
                }
                // LOGIN WITH MAGIC LINK WITH EMAIL
                if (output.email) {
                    await magic.auth.loginWithMagicLink({
                        email: output.email,
                    });
                }
                const signer = await this.getSigner();
                const account = await signer.getAddress();
                return {
                    account,
                    chain: {
                        id: 0,
                        unsupported: false,
                    },
                    provider,
                };
            }
            throw new UserRejectedRequestError('User rejected request');
        }
        catch (error) {
            throw new UserRejectedRequestError();
        }
    }
    async getAccount() {
        const provider = new ethers.providers.Web3Provider(this.getProvider());
        const signer = provider.getSigner();
        const account = await signer.getAddress();
        return account;
    }
    async getUserDetailsByForm() {
        this.isModalOpen = true;
        const output = (await createModal({
            accentColor: this.accentColor,
            isDarkMode: this.isDarkMode,
            customLogo: this.customLogo,
            customHeaderText: this.customHeaderText,
            oauthProviders: this.oauthProviders,
        }));
        this.isModalOpen = false;
        return output;
    }
    getProvider() {
        if (this.provider) {
            return this.provider;
        }
        const magic = this.getMagicSDK();
        this.provider = magic.rpcProvider;
        return this.provider;
    }
    async getSigner() {
        const provider = new ethers.providers.Web3Provider(this.getProvider());
        const signer = await provider.getSigner();
        return signer;
    }
    async isAuthorized() {
        try {
            const account = await this.getAccount();
            return !!account;
        }
        catch {
            return false;
        }
    }
    getMagicSDK() {
        if (!this.magicSDK) {
            this.magicSDK = new Magic(this.magicOptions.apiKey, {
                ...this.magicOptions.additionalMagicOptions,
                extensions: [new OAuthExtension()],
            });
            return this.magicSDK;
        }
        return this.magicSDK;
    }
    async getChainId() {
        throw new Error('Method not implemented.');
    }
    onAccountsChanged(accounts) {
        if (accounts.length === 0)
            this.emit('disconnect');
        else
            this.emit('change', { account: getAddress(accounts[0]) });
    }
    onChainChanged(chainId) {
        const id = normalizeChainId(chainId);
        const unsupported = this.isChainUnsupported(id);
        this.emit('change', { chain: { id, unsupported } });
    }
    onDisconnect() {
        this.emit('disconnect');
    }
    async disconnect() {
        const magic = this.getMagicSDK();
        await magic.user.logout();
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY29ubmVjdG9yLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2xpYi9jb25uZWN0b3IudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUEsT0FBTyxFQUFFLGNBQWMsRUFBaUIsTUFBTSxrQkFBa0IsQ0FBQztBQU1qRSxPQUFPLEVBRUwsU0FBUyxFQUNULGdCQUFnQixFQUNoQix3QkFBd0IsR0FDekIsTUFBTSxhQUFhLENBQUM7QUFDckIsT0FBTyxFQUFFLE1BQU0sRUFBVSxNQUFNLFFBQVEsQ0FBQztBQUN4QyxPQUFPLEVBQUUsVUFBVSxFQUFFLE1BQU0sa0JBQWtCLENBQUM7QUFDOUMsT0FBTyxFQUFFLEtBQUssRUFBRSxNQUFNLFdBQVcsQ0FBQztBQUVsQyxPQUFPLEVBQUUsV0FBVyxFQUFFLE1BQU0sY0FBYyxDQUFDO0FBRTNDLE1BQU0sU0FBUyxHQUFHLE9BQU8sTUFBTSxLQUFLLFdBQVcsQ0FBQztBQXVCaEQsTUFBTSxPQUFPLGNBQWUsU0FBUSxTQUFTO0lBQzNDLEtBQUssR0FBRyxDQUFDLFNBQVMsQ0FBQztJQUVWLEVBQUUsR0FBRyxPQUFPLENBQUM7SUFFYixJQUFJLEdBQUcsT0FBTyxDQUFDO0lBRXhCLFFBQVEsQ0FBQztJQUVULFFBQVEsQ0FBZ0U7SUFFeEUsV0FBVyxHQUFHLEtBQUssQ0FBQztJQUVwQixZQUFZLENBQVU7SUFFdEIsV0FBVyxDQUFxQjtJQUVoQyxVQUFVLENBQXNCO0lBRWhDLFVBQVUsQ0FBcUI7SUFFL0IsZ0JBQWdCLENBQXFCO0lBRXJDLGNBQWMsQ0FBa0I7SUFFaEMsZ0JBQWdCLENBQXFCO0lBRXJDLFlBQVksTUFBMEQ7UUFDcEUsS0FBSyxDQUFDLE1BQU0sQ0FBQyxDQUFDO1FBQ2QsSUFBSSxDQUFDLFlBQVksR0FBRyxNQUFNLENBQUMsT0FBTyxDQUFDO1FBQ25DLElBQUksQ0FBQyxXQUFXLEdBQUcsTUFBTSxDQUFDLE9BQU8sQ0FBQyxXQUFXLENBQUM7UUFDOUMsSUFBSSxDQUFDLFVBQVUsR0FBRyxNQUFNLENBQUMsT0FBTyxDQUFDLFVBQVUsQ0FBQztRQUM1QyxJQUFJLENBQUMsVUFBVSxHQUFHLE1BQU0sQ0FBQyxPQUFPLENBQUMsVUFBVSxDQUFDO1FBQzVDLElBQUksQ0FBQyxnQkFBZ0IsR0FBRyxNQUFNLENBQUMsT0FBTyxDQUFDLGdCQUFnQixDQUFDO1FBQ3hELElBQUksQ0FBQyxjQUFjLEdBQUcsTUFBTSxDQUFDLE9BQU8sQ0FBQyxZQUFZLEVBQUUsU0FBUyxJQUFJLEVBQUUsQ0FBQztRQUNuRSxJQUFJLENBQUMsZ0JBQWdCLEdBQUcsTUFBTSxDQUFDLE9BQU8sQ0FBQyxZQUFZLEVBQUUsV0FBVyxDQUFDO0lBQ25FLENBQUM7SUFFRCxLQUFLLENBQUMsT0FBTztRQUNYLElBQUk7WUFDRixNQUFNLFFBQVEsR0FBRyxNQUFNLElBQUksQ0FBQyxXQUFXLEVBQUUsQ0FBQztZQUUxQyxJQUFJLFFBQVEsQ0FBQyxFQUFFLEVBQUU7Z0JBQ2YsUUFBUSxDQUFDLEVBQUUsQ0FBQyxpQkFBaUIsRUFBRSxJQUFJLENBQUMsaUJBQWlCLENBQUMsQ0FBQztnQkFDdkQsUUFBUSxDQUFDLEVBQUUsQ0FBQyxjQUFjLEVBQUUsSUFBSSxDQUFDLGNBQWMsQ0FBQyxDQUFDO2dCQUNqRCxRQUFRLENBQUMsRUFBRSxDQUFDLFlBQVksRUFBRSxJQUFJLENBQUMsWUFBWSxDQUFDLENBQUM7YUFDOUM7WUFFRCxxQ0FBcUM7WUFDckMsTUFBTSxlQUFlLEdBQUcsTUFBTSxJQUFJLENBQUMsWUFBWSxFQUFFLENBQUM7WUFFbEQsZ0RBQWdEO1lBQ2hELElBQUksZUFBZSxFQUFFO2dCQUNuQixPQUFPO29CQUNMLFFBQVE7b0JBQ1IsS0FBSyxFQUFFO3dCQUNMLEVBQUUsRUFBRSxDQUFDO3dCQUNMLFdBQVcsRUFBRSxLQUFLO3FCQUNuQjtvQkFDRCxPQUFPLEVBQUUsTUFBTSxJQUFJLENBQUMsVUFBVSxFQUFFO2lCQUNqQyxDQUFDO2FBQ0g7WUFFRCxtREFBbUQ7WUFDbkQsSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLEVBQUU7Z0JBQ3JCLE1BQU0sTUFBTSxHQUFHLE1BQU0sSUFBSSxDQUFDLG9CQUFvQixFQUFFLENBQUM7Z0JBQ2pELE1BQU0sS0FBSyxHQUFHLElBQUksQ0FBQyxXQUFXLEVBQUUsQ0FBQztnQkFFakMsNENBQTRDO2dCQUM1QyxJQUFJLE1BQU0sQ0FBQyxhQUFhLEVBQUU7b0JBQ3hCLE1BQU0sS0FBSyxDQUFDLEtBQUssQ0FBQyxpQkFBaUIsQ0FBQzt3QkFDbEMsUUFBUSxFQUFFLE1BQU0sQ0FBQyxhQUFhO3dCQUM5QixXQUFXLEVBQUUsSUFBSSxDQUFDLGdCQUFnQixJQUFJLE1BQU0sQ0FBQyxRQUFRLENBQUMsSUFBSTtxQkFDM0QsQ0FBQyxDQUFDO2lCQUNKO2dCQUVELG1DQUFtQztnQkFDbkMsSUFBSSxNQUFNLENBQUMsS0FBSyxFQUFFO29CQUNoQixNQUFNLEtBQUssQ0FBQyxJQUFJLENBQUMsa0JBQWtCLENBQUM7d0JBQ2xDLEtBQUssRUFBRSxNQUFNLENBQUMsS0FBSztxQkFDcEIsQ0FBQyxDQUFDO2lCQUNKO2dCQUVELE1BQU0sTUFBTSxHQUFHLE1BQU0sSUFBSSxDQUFDLFNBQVMsRUFBRSxDQUFDO2dCQUN0QyxNQUFNLE9BQU8sR0FBRyxNQUFNLE1BQU0sQ0FBQyxVQUFVLEVBQUUsQ0FBQztnQkFFMUMsT0FBTztvQkFDTCxPQUFPO29CQUNQLEtBQUssRUFBRTt3QkFDTCxFQUFFLEVBQUUsQ0FBQzt3QkFDTCxXQUFXLEVBQUUsS0FBSztxQkFDbkI7b0JBQ0QsUUFBUTtpQkFDVCxDQUFDO2FBQ0g7WUFDRCxNQUFNLElBQUksd0JBQXdCLENBQUMsdUJBQXVCLENBQUMsQ0FBQztTQUM3RDtRQUFDLE9BQU8sS0FBSyxFQUFFO1lBQ2QsTUFBTSxJQUFJLHdCQUF3QixFQUFFLENBQUM7U0FDdEM7SUFDSCxDQUFDO0lBRUQsS0FBSyxDQUFDLFVBQVU7UUFDZCxNQUFNLFFBQVEsR0FBRyxJQUFJLE1BQU0sQ0FBQyxTQUFTLENBQUMsWUFBWSxDQUFDLElBQUksQ0FBQyxXQUFXLEVBQUUsQ0FBQyxDQUFDO1FBQ3ZFLE1BQU0sTUFBTSxHQUFHLFFBQVEsQ0FBQyxTQUFTLEVBQUUsQ0FBQztRQUNwQyxNQUFNLE9BQU8sR0FBRyxNQUFNLE1BQU0sQ0FBQyxVQUFVLEVBQUUsQ0FBQztRQUMxQyxPQUFPLE9BQU8sQ0FBQztJQUNqQixDQUFDO0lBRUQsS0FBSyxDQUFDLG9CQUFvQjtRQUN4QixJQUFJLENBQUMsV0FBVyxHQUFHLElBQUksQ0FBQztRQUV4QixNQUFNLE1BQU0sR0FBZ0IsQ0FBQyxNQUFNLFdBQVcsQ0FBQztZQUM3QyxXQUFXLEVBQUUsSUFBSSxDQUFDLFdBQVc7WUFDN0IsVUFBVSxFQUFFLElBQUksQ0FBQyxVQUFVO1lBQzNCLFVBQVUsRUFBRSxJQUFJLENBQUMsVUFBVTtZQUMzQixnQkFBZ0IsRUFBRSxJQUFJLENBQUMsZ0JBQWdCO1lBQ3ZDLGNBQWMsRUFBRSxJQUFJLENBQUMsY0FBYztTQUNwQyxDQUFDLENBQWdCLENBQUM7UUFFbkIsSUFBSSxDQUFDLFdBQVcsR0FBRyxLQUFLLENBQUM7UUFDekIsT0FBTyxNQUFNLENBQUM7SUFDaEIsQ0FBQztJQUVELFdBQVc7UUFDVCxJQUFJLElBQUksQ0FBQyxRQUFRLEVBQUU7WUFDakIsT0FBTyxJQUFJLENBQUMsUUFBUSxDQUFDO1NBQ3RCO1FBQ0QsTUFBTSxLQUFLLEdBQUcsSUFBSSxDQUFDLFdBQVcsRUFBRSxDQUFDO1FBQ2pDLElBQUksQ0FBQyxRQUFRLEdBQUcsS0FBSyxDQUFDLFdBQVcsQ0FBQztRQUNsQyxPQUFPLElBQUksQ0FBQyxRQUFRLENBQUM7SUFDdkIsQ0FBQztJQUVELEtBQUssQ0FBQyxTQUFTO1FBQ2IsTUFBTSxRQUFRLEdBQUcsSUFBSSxNQUFNLENBQUMsU0FBUyxDQUFDLFlBQVksQ0FBQyxJQUFJLENBQUMsV0FBVyxFQUFFLENBQUMsQ0FBQztRQUN2RSxNQUFNLE1BQU0sR0FBRyxNQUFNLFFBQVEsQ0FBQyxTQUFTLEVBQUUsQ0FBQztRQUMxQyxPQUFPLE1BQU0sQ0FBQztJQUNoQixDQUFDO0lBRUQsS0FBSyxDQUFDLFlBQVk7UUFDaEIsSUFBSTtZQUNGLE1BQU0sT0FBTyxHQUFHLE1BQU0sSUFBSSxDQUFDLFVBQVUsRUFBRSxDQUFDO1lBQ3hDLE9BQU8sQ0FBQyxDQUFDLE9BQU8sQ0FBQztTQUNsQjtRQUFDLE1BQU07WUFDTixPQUFPLEtBQUssQ0FBQztTQUNkO0lBQ0gsQ0FBQztJQUVELFdBQVc7UUFDVCxJQUFJLENBQUMsSUFBSSxDQUFDLFFBQVEsRUFBRTtZQUNsQixJQUFJLENBQUMsUUFBUSxHQUFHLElBQUksS0FBSyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsTUFBTSxFQUFFO2dCQUNsRCxHQUFHLElBQUksQ0FBQyxZQUFZLENBQUMsc0JBQXNCO2dCQUMzQyxVQUFVLEVBQUUsQ0FBQyxJQUFJLGNBQWMsRUFBRSxDQUFDO2FBQ25DLENBQUMsQ0FBQztZQUNILE9BQU8sSUFBSSxDQUFDLFFBQVEsQ0FBQztTQUN0QjtRQUNELE9BQU8sSUFBSSxDQUFDLFFBQVEsQ0FBQztJQUN2QixDQUFDO0lBRUQsS0FBSyxDQUFDLFVBQVU7UUFDZCxNQUFNLElBQUksS0FBSyxDQUFDLHlCQUF5QixDQUFDLENBQUM7SUFDN0MsQ0FBQztJQUVTLGlCQUFpQixDQUFDLFFBQWtCO1FBQzVDLElBQUksUUFBUSxDQUFDLE1BQU0sS0FBSyxDQUFDO1lBQUUsSUFBSSxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsQ0FBQzs7WUFDOUMsSUFBSSxDQUFDLElBQUksQ0FBQyxRQUFRLEVBQUUsRUFBRSxPQUFPLEVBQUUsVUFBVSxDQUFDLFFBQVEsQ0FBQyxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQztJQUNqRSxDQUFDO0lBRVMsY0FBYyxDQUFDLE9BQXdCO1FBQy9DLE1BQU0sRUFBRSxHQUFHLGdCQUFnQixDQUFDLE9BQU8sQ0FBQyxDQUFDO1FBQ3JDLE1BQU0sV0FBVyxHQUFHLElBQUksQ0FBQyxrQkFBa0IsQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUNoRCxJQUFJLENBQUMsSUFBSSxDQUFDLFFBQVEsRUFBRSxFQUFFLEtBQUssRUFBRSxFQUFFLEVBQUUsRUFBRSxXQUFXLEVBQUUsRUFBRSxDQUFDLENBQUM7SUFDdEQsQ0FBQztJQUVTLFlBQVk7UUFDcEIsSUFBSSxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsQ0FBQztJQUMxQixDQUFDO0lBRUQsS0FBSyxDQUFDLFVBQVU7UUFDZCxNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMsV0FBVyxFQUFFLENBQUM7UUFDakMsTUFBTSxLQUFLLENBQUMsSUFBSSxDQUFDLE1BQU0sRUFBRSxDQUFDO0lBQzVCLENBQUM7Q0FDRiJ9